import { useState } from "react";
import { toast } from "react-hot-toast";

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from "../../components/ui";
import { useCreateAgentCompany, type AgentCompany } from "../api/e2e";
import CountrySelect from "./CountrySelect";

export default function CreateCompanyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (company: AgentCompany) => void;
}) {
  const createCompany = useCreateAgentCompany();
  const [ticker, setTicker] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("US");

  const submit = async () => {
    const normalizedTicker = ticker.trim().toUpperCase();
    const normalizedName = companyName.trim();
    const normalizedCountry = country.trim().toUpperCase();
    if (
      !normalizedTicker ||
      !normalizedName ||
      !/^[A-Z]{2}$/.test(normalizedCountry)
    ) {
      toast.error("Enter a ticker, company name, and two-letter country code.");
      return;
    }
    try {
      const company = await createCompany.mutateAsync({
        ticker: normalizedTicker,
        company_name: normalizedName,
        country: normalizedCountry,
      });
      onCreated?.(company);
      toast.success(`${normalizedTicker} was created.`);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "The company could not be created.",
      );
    }
  };

  return (
    <Modal
      size="compact"
      ariaLabelledBy="create-company-title"
      onClose={onClose}
    >
      <ModalHeader
        eyebrow="Company catalog"
        title="Create company"
        titleId="create-company-title"
        onClose={onClose}
      />
      <ModalBody className="agent-field-grid">
        <TextInput
          label="Ticker"
          value={ticker}
          onValueChange={setTicker}
          placeholder="CNK"
          className="agent-field-wide"
          autoFocus
        />
        <TextInput
          label="Company name"
          value={companyName}
          onValueChange={setCompanyName}
          placeholder="Cinemark Holdings, Inc."
          className="agent-field-wide"
        />
        <label className="agent-field-wide">
          <span>Country</span>
          <CountrySelect
            value={country}
            onChange={setCountry}
            disabled={createCompany.isPending}
          />
        </label>
      </ModalBody>
      <ModalFooter>
        <Button tone="quiet" onClick={onClose}>
          Cancel
        </Button>
        <Button
          tone="primary"
          disabled={createCompany.isPending}
          onClick={() => void submit()}
        >
          {createCompany.isPending ? "Creating…" : "Create company"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
